/**
 * UserRowActions Component
 *
 * Action buttons for a regular user row in UsersTable.
 * - `variant="dropdown"` renders a ⋮ dropdown menu (mobile).
 * - `variant="inline"` renders icon buttons with tooltips (desktop).
 */
import { Button, Dropdown, Icon, Tooltip } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { UserData } from '../types';

interface UserRowActionsProps {
  user: UserData;
  isCurrentUser: boolean;
  variant: 'dropdown' | 'inline';
  onEdit: (user: UserData) => void;
  onDeactivate: (user: UserData) => void;
  onReactivate: (user: UserData) => void;
  onDelete: (user: UserData) => void;
}

export function UserRowActions({
  user,
  isCurrentUser,
  variant,
  onEdit,
  onDeactivate,
  onReactivate,
  onDelete
}: UserRowActionsProps) {
  const t = useFeatureTranslation('systemSettings.users');
  const isActive = user.status === 'ACTIVE';

  // System users cannot be managed; the current user cannot deactivate/reactivate/delete themselves.
  const isDisabled = isCurrentUser || user.isSystemUser;
  // The current user may edit their own profile (email stays read-only in the modal).
  const isEditDisabled = user.isSystemUser;

  if (variant === 'dropdown') {
    return (
      <Dropdown
        triggerButton={
          <Button variant='ghost' size='sm' aria-label={t('fields.actions')}>
            <span className='text-lg leading-none tracking-widest select-none'>⋮</span>
          </Button>
        }
        align='bottom end'
        size='sm'
      >
        <Dropdown.Item icon='pen' onClick={() => onEdit(user)} disabled={isEditDisabled}>
          {t('actions.edit')}
        </Dropdown.Item>

        {isActive ? (
          <Dropdown.Item
            icon='circle-xmark'
            onClick={() => onDeactivate(user)}
            disabled={isDisabled}
          >
            {t('actions.deactivate')}
          </Dropdown.Item>
        ) : (
          <Dropdown.Item
            icon='circle-check'
            onClick={() => onReactivate(user)}
            disabled={isDisabled}
          >
            {t('actions.reactivate')}
          </Dropdown.Item>
        )}

        <Dropdown.Item
          icon='trash'
          onClick={() => onDelete(user)}
          disabled={isDisabled}
          className='text-error-text'
        >
          {t('actions.delete')}
        </Dropdown.Item>
      </Dropdown>
    );
  }

  return (
    <div className='flex gap-xs justify-end'>
      {isActive ? (
        <Tooltip label={t('actions.deactivate')} position='top'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => onDeactivate(user)}
            aria-label={t('actions.deactivate')}
            disabled={isDisabled}
          >
            <Icon name='circle-xmark' size='sm' className='text-primary-base' />
          </Button>
        </Tooltip>
      ) : (
        <Tooltip label={t('actions.reactivate')} position='top'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => onReactivate(user)}
            aria-label={t('actions.reactivate')}
            disabled={isDisabled}
          >
            <Icon name='circle-check' size='sm' className='text-primary-base' />
          </Button>
        </Tooltip>
      )}

      <Tooltip label={t('actions.edit')} position='top'>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => onEdit(user)}
          aria-label={t('actions.edit')}
          disabled={isEditDisabled}
        >
          <Icon name='pen' size='sm' className='text-primary-base' />
        </Button>
      </Tooltip>

      <Tooltip label={t('actions.delete')} position='top'>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => onDelete(user)}
          aria-label={t('actions.delete')}
          disabled={isDisabled}
        >
          <Icon name='trash' size='sm' className='text-error-text' />
        </Button>
      </Tooltip>
    </div>
  );
}
